import { useEffect, useState } from 'react';

import { fetchApi } from '../../Network/NetworkService';
import { notif } from '../Ui/Notification';

const FIELDS = [
    {
        key: 'basePriceNew',
        label: 'Consultation',
        sub: 'Nouveau patient',
        icon: '🩺',
        accent: '#6366f1',
    },
    {
        key: 'basePriceRecurrent',
        label: 'Consultation',
        sub: 'Patient récurrent',
        icon: '💚',
        accent: '#10b981',
    },
    {
        key: 'skycallVoicePrice',
        label: 'Skycall',
        sub: 'Voice',
        icon: '📞',
        accent: '#0ea5e9',
    },
    {
        key: 'skycallDefaultPrice',
        label: 'Skycall',
        sub: 'Défaut',
        icon: '☎️',
        accent: '#8b5cf6',
    },
    {
        key: 'prioritySurcharge',
        label: 'Supplément',
        sub: 'Prioritaire (+5€)',
        icon: '⚡',
        accent: '#f59e0b',
    },
];

// Markets a specialty price can be overridden for. Mirrors OVERRIDE_POOLS in
// server/Controllers/PricingConfig.controller.js. The empty value is not a
// market — it means "every market", i.e. the plain `tiers` map. FR is absent on
// purpose: it is the default market, so its price already IS that map.
const POOL_OPTIONS = [
    { value: '', label: 'Tous les marchés', accent: '#94a3b8' },
    { value: 'ES', label: '🇪🇸  Espagne (ES)', accent: '#f59e0b' },
    { value: 'IL', label: '🇮🇱  Israël (IL)', accent: '#0ea5e9' },
];

const poolAccent = (pool) => (POOL_OPTIONS.find((p) => p.value === pool) || POOL_OPTIONS[0]).accent;

const centsToEuros = (cents) => (cents == null ? '' : (cents / 100).toFixed(2));
const eurosToCents = (str) => Math.round(Number(str) * 100);

const PricingConfig = () => {
    const [pricing, setPricing] = useState(null);
    const [form, setForm] = useState({});
    const [tiers, setTiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');

    const load = () => {
        setLoading(true);
        fetchApi('/pricing-config')
            .then((res) => {
                if (!res || !res.success) throw new Error('Réponse invalide');
                setPricing(res.pricing);
                const next = {};
                FIELDS.forEach(({ key }) => { next[key] = centsToEuros(res.pricing[key]); });
                setForm(next);
                // One flat table over two stored shapes: `tiers` (every market)
                // and `poolTiers` (one market each). Rows carry the market they
                // came from and handleSave splits them back apart.
                const tiersObj = res.pricing.tiers || {};
                const poolTiersObj = res.pricing.poolTiers || {};
                const rows = Object.entries(tiersObj)
                    .map(([slug, cents]) => ({ pool: '', slug, euros: centsToEuros(cents) }));
                for (const [pool, overlay] of Object.entries(poolTiersObj)) {
                    for (const [slug, cents] of Object.entries(overlay || {})) {
                        rows.push({ pool, slug, euros: centsToEuros(cents) });
                    }
                }
                // Group by specialty so an override sits directly under the
                // global price it replaces — that adjacency is the whole point
                // of putting both in one table.
                rows.sort((a, b) => a.slug.localeCompare(b.slug) || a.pool.localeCompare(b.pool));
                setTiers(rows);
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                notif.addNotification('Erreur', `Chargement impossible: ${err.message || err}`, 'warning');
            });
    };

    useEffect(load, []);

    const handleChange = (key) => (e) => {
        setForm({ ...form, [key]: e.target.value });
    };

    const handleTierChange = (index, field) => (e) => {
        const next = [...tiers];
        next[index] = { ...next[index], [field]: e.target.value };
        setTiers(next);
    };

    const handleAddTier = () => {
        setTiers([...tiers, { pool: '', slug: '', euros: '' }]);
    };

    const handleRemoveTier = (index) => () => {
        setTiers(tiers.filter((_, i) => i !== index));
    };

    const handleSave = () => {
        const body = {};
        for (const { key, label, sub } of FIELDS) {
            const cents = eurosToCents(form[key]);
            if (!Number.isFinite(cents) || cents < 0 || !Number.isInteger(cents)) {
                notif.addNotification('Erreur', `${label} — ${sub}: valeur invalide.`, 'warning');
                return;
            }
            body[key] = cents;
        }

        const tiersOut = {};
        const poolTiersOut = {};
        const seen = new Set();
        for (const { pool, slug, euros } of tiers) {
            const market = (pool || '').trim().toUpperCase();
            // Lowercase to match the slug the questionnaire actually sends —
            // "Dermatologie" would save fine and then never apply.
            const trimmed = (slug || '').trim().toLowerCase();
            if (!trimmed && (euros === '' || euros == null)) continue;
            if (!trimmed) {
                notif.addNotification('Erreur', 'Un tarif spécialité a un slug vide.', 'warning');
                return;
            }
            if (!/^[a-z0-9-]+$/i.test(trimmed)) {
                notif.addNotification('Erreur', `Slug invalide: ${trimmed}`, 'warning');
                return;
            }
            if (market && !POOL_OPTIONS.some((p) => p.value === market)) {
                notif.addNotification('Erreur', `Marché inconnu: ${market}`, 'warning');
                return;
            }
            // Uniqueness is per (marché, slug): the same specialty may legitimately
            // appear once globally and once per market.
            const dupKey = `${market} ${trimmed}`;
            if (seen.has(dupKey)) {
                const where = market ? ` pour le marché ${market}` : '';
                notif.addNotification('Erreur', `Slug en double: ${trimmed}${where}`, 'warning');
                return;
            }
            seen.add(dupKey);
            const cents = eurosToCents(euros);
            if (!Number.isFinite(cents) || cents < 1 || !Number.isInteger(cents)) {
                notif.addNotification('Erreur', `${trimmed}: montant invalide.`, 'warning');
                return;
            }
            if (market) {
                if (!poolTiersOut[market]) poolTiersOut[market] = {};
                poolTiersOut[market][trimmed] = cents;
            } else {
                tiersOut[trimmed] = cents;
            }
        }
        body.tiers = tiersOut;
        body.poolTiers = poolTiersOut;

        setSaving(true);
        fetchApi('/pricing-config', { method: 'PUT', body: JSON.stringify(body) })
            .then((res) => {
                setSaving(false);
                if (!res || !res.success) throw new Error(res && res.message);
                setPricing(res.pricing);
                notif.addNotification('Succès', 'Tarifs mis à jour.', 'success');
            })
            .catch((err) => {
                setSaving(false);
                notif.addNotification('Erreur', `Sauvegarde impossible: ${err.message || err}`, 'warning');
            });
    };

    const filteredTiers = tiers
        .map((t, i) => ({ ...t, _idx: i }))
        // Match the market too, so "ES" pulls up every Spanish override at once.
        .filter((t) => !search || `${t.slug} ${t.pool || ''}`.toLowerCase().includes(search.toLowerCase()));

    const tiersCount = tiers.filter((t) => (t.slug || '').trim()).length;

    return (
        <div style={{ padding: '8px 4px 40px' }}>
            {/* Hero header */}
            <div
                style={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
                    borderRadius: 16,
                    padding: '28px 32px',
                    color: 'white',
                    boxShadow: '0 10px 30px -10px rgba(99, 102, 241, 0.5)',
                    marginBottom: 24,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        right: -40,
                        top: -40,
                        width: 200,
                        height: 200,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.08)',
                    }}
                />
                <div
                    style={{
                        position: 'absolute',
                        right: 80,
                        bottom: -60,
                        width: 140,
                        height: 140,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.06)',
                    }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, position: 'relative' }}>
                    <div
                        style={{
                            width: 56,
                            height: 56,
                            borderRadius: 14,
                            background: 'rgba(255,255,255,0.18)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 28,
                            backdropFilter: 'blur(8px)',
                        }}
                    >
                        💸
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontWeight: 700, fontSize: 24, color: '#fff' }}>Tarifs</h2>
                        <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 14, color: '#fff' }}>
                            Configurez les prix des consultations et des spécialités. Les changements peuvent prendre jusqu'à 60s avant d'être appliqués côté patient.
                        </p>
                    </div>
                </div>
            </div>

            {loading && <span className="loader" />}

            {!loading && (
                <>
                    {/* Default pricing cards */}
                    <div style={{ marginBottom: 28 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                                Tarifs par défaut
                            </h3>
                            <span style={{ fontSize: 12, color: '#94a3b8' }}>5 tarifs</span>
                        </div>

                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                gap: 14,
                            }}
                        >
                            {FIELDS.map(({ key, label, sub, icon, accent }) => (
                                <div
                                    key={key}
                                    style={{
                                        background: 'white',
                                        borderRadius: 14,
                                        padding: 18,
                                        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)',
                                        border: '1px solid #eef2f7',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 12px 24px -12px rgba(15, 23, 42, 0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)';
                                    }}
                                >
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: 4,
                                            height: '100%',
                                            background: accent,
                                        }}
                                    />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                                        <div
                                            style={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: 10,
                                                background: `${accent}15`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 18,
                                            }}
                                        >
                                            {icon}
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                                                {label}
                                            </div>
                                            <div style={{ fontSize: 12, color: '#64748b' }}>{sub}</div>
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            background: '#f8fafc',
                                            borderRadius: 10,
                                            border: '1px solid #e2e8f0',
                                            padding: '4px 12px',
                                            transition: 'border-color 0.15s',
                                        }}
                                    >
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={form[key] || ''}
                                            onChange={handleChange(key)}
                                            disabled={saving}
                                            style={{
                                                flex: 1,
                                                border: 'none',
                                                background: 'transparent',
                                                fontSize: 22,
                                                fontWeight: 700,
                                                color: '#0f172a',
                                                outline: 'none',
                                                padding: '8px 0',
                                                width: '100%',
                                            }}
                                            onFocus={(e) => { e.target.parentElement.style.borderColor = accent; }}
                                            onBlur={(e) => { e.target.parentElement.style.borderColor = '#e2e8f0'; }}
                                        />
                                        <span style={{ color: accent, fontWeight: 700, fontSize: 16 }}>€</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Specialty tiers */}
                    <div
                        style={{
                            background: 'white',
                            borderRadius: 16,
                            padding: 24,
                            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                            border: '1px solid #eef2f7',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 12,
                                marginBottom: 4,
                            }}
                        >
                            <div>
                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span>🎯</span> Tarifs spécialité
                                    <span
                                        style={{
                                            background: '#eef2ff',
                                            color: '#6366f1',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            padding: '2px 10px',
                                            borderRadius: 999,
                                        }}
                                    >
                                        {tiersCount}
                                    </span>
                                </h3>
                                <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
                                    Les slugs absents de cette liste sont facturés au tarif par défaut ci-dessus (avec logique récurrent / nouveau patient).
                                    Une ligne rattachée à un marché ne remplace que cette spécialité pour ce marché&nbsp;: tout le reste garde le tarif «&nbsp;Tous les marchés&nbsp;».
                                </p>
                            </div>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="🔍 Rechercher une spécialité..."
                                style={{
                                    padding: '8px 14px',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 10,
                                    background: '#f8fafc',
                                    fontSize: 13,
                                    outline: 'none',
                                    minWidth: 240,
                                }}
                            />
                        </div>

                        <div style={{ marginTop: 16, overflow: 'hidden', borderRadius: 12, border: '1px solid #eef2f7' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc' }}>
                                        <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Spécialité (slug)
                                        </th>
                                        <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, width: 200 }}>
                                            Marché
                                        </th>
                                        <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, width: 180 }}>
                                            Montant
                                        </th>
                                        <th style={{ width: 50 }} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTiers.length === 0 && (
                                        <tr>
                                            <td colSpan="4" style={{ padding: '32px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                                                {search ? 'Aucune spécialité ne correspond à votre recherche.' : 'Aucun tarif spécialité défini.'}
                                            </td>
                                        </tr>
                                    )}
                                    {filteredTiers.map((row) => {
                                        const i = row._idx;
                                        return (
                                            <tr
                                                key={i}
                                                style={{ borderTop: '1px solid #f1f5f9', transition: 'background 0.1s' }}
                                                onMouseEnter={(e) => { e.currentTarget.style.background = '#fafbff'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                            >
                                                <td style={{ padding: '10px 16px' }}>
                                                    <input
                                                        type="text"
                                                        value={row.slug}
                                                        onChange={handleTierChange(i, 'slug')}
                                                        placeholder="ex: dermatologie"
                                                        disabled={saving}
                                                        style={{
                                                            width: '100%',
                                                            padding: '8px 12px',
                                                            border: '1px solid #e2e8f0',
                                                            borderRadius: 8,
                                                            fontSize: 13,
                                                            outline: 'none',
                                                            background: 'white',
                                                        }}
                                                    />
                                                </td>
                                                <td style={{ padding: '10px 16px' }}>
                                                    <select
                                                        value={row.pool || ''}
                                                        onChange={handleTierChange(i, 'pool')}
                                                        disabled={saving}
                                                        style={{
                                                            width: '100%',
                                                            padding: '8px 10px',
                                                            border: '1px solid #e2e8f0',
                                                            borderLeft: `3px solid ${poolAccent(row.pool || '')}`,
                                                            borderRadius: 8,
                                                            fontSize: 13,
                                                            fontWeight: row.pool ? 700 : 400,
                                                            color: row.pool ? '#0f172a' : '#64748b',
                                                            outline: 'none',
                                                            background: 'white',
                                                            cursor: saving ? 'not-allowed' : 'pointer',
                                                        }}
                                                    >
                                                        {POOL_OPTIONS.map((p) => (
                                                            <option key={p.value} value={p.value}>{p.label}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td style={{ padding: '10px 16px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0 12px', background: 'white' }}>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={row.euros}
                                                            onChange={handleTierChange(i, 'euros')}
                                                            disabled={saving}
                                                            style={{
                                                                width: '100%',
                                                                padding: '8px 0',
                                                                border: 'none',
                                                                fontSize: 14,
                                                                fontWeight: 600,
                                                                color: '#0f172a',
                                                                outline: 'none',
                                                                background: 'transparent',
                                                            }}
                                                        />
                                                        <span style={{ color: '#6366f1', fontWeight: 700 }}>€</span>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                                                    <button
                                                        type="button"
                                                        onClick={handleRemoveTier(i)}
                                                        disabled={saving}
                                                        title="Supprimer"
                                                        style={{
                                                            background: '#fef2f2',
                                                            border: 'none',
                                                            color: '#ef4444',
                                                            cursor: 'pointer',
                                                            fontSize: 18,
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: 8,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            transition: 'background 0.15s',
                                                            lineHeight: 1,
                                                        }}
                                                        onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                                                        onMouseLeave={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
                                                    >
                                                        ×
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <button
                            type="button"
                            onClick={handleAddTier}
                            disabled={saving}
                            style={{
                                marginTop: 14,
                                background: 'white',
                                border: '1.5px dashed #c7d2fe',
                                padding: '10px 16px',
                                borderRadius: 10,
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: 600,
                                color: '#6366f1',
                                transition: 'all 0.15s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#eef2ff';
                                e.currentTarget.style.borderColor = '#6366f1';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.borderColor = '#c7d2fe';
                            }}
                        >
                            + Ajouter un tarif spécialité
                        </button>
                    </div>

                    {/* Sticky action bar */}
                    <div
                        style={{
                            marginTop: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 12,
                            background: 'white',
                            border: '1px solid #eef2f7',
                            borderRadius: 14,
                            padding: '14px 20px',
                            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
                        }}
                    >
                        {pricing && pricing.updatedAt ? (
                            <div style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
                                Dernière modification le{' '}
                                <strong style={{ color: '#334155' }}>
                                    {new Date(pricing.updatedAt).toLocaleString('fr-FR')}
                                </strong>
                                {pricing.updatedBy ? <> par <strong style={{ color: '#334155' }}>{pricing.updatedBy}</strong></> : null}
                            </div>
                        ) : <span />}
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            style={{
                                background: saving
                                    ? '#94a3b8'
                                    : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                color: 'white',
                                border: 'none',
                                padding: '12px 28px',
                                borderRadius: 10,
                                fontWeight: 700,
                                fontSize: 14,
                                cursor: saving ? 'not-allowed' : 'pointer',
                                boxShadow: saving ? 'none' : '0 4px 12px rgba(99, 102, 241, 0.35)',
                                transition: 'transform 0.15s, box-shadow 0.15s',
                            }}
                            onMouseEnter={(e) => {
                                if (saving) return;
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(99, 102, 241, 0.45)';
                            }}
                            onMouseLeave={(e) => {
                                if (saving) return;
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.35)';
                            }}
                        >
                            {saving ? 'Sauvegarde...' : '💾  Enregistrer les modifications'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default PricingConfig;
